import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { getMyPermissions } from "../services/api";

interface PermissionState {
  permissions: string[];
  role: string | null;
  roleId: number | null;
  loaded: boolean;
  /** Rol için izinler tanımlıysa true. Değilse (henüz kurulmadıysa) sistem permissive davranır. */
  configured: boolean;
  /** Belirli granular izin kodu var mı? Örn. has("cost:categories:create") */
  has: (code: string) => boolean;
  /** Ekrana erişim (o ekran altında herhangi bir izin var mı?) */
  hasScreen: (screenCode: string) => boolean;
  refresh: () => Promise<void>;
}

const PermissionContext = createContext<PermissionState | undefined>(undefined);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [role, setRole] = useState<string | null>(null);
  const [roleId, setRoleId] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getMyPermissions();
      setPermissions(res.data?.permissions || []);
      setRole(res.data?.role ?? null);
      setRoleId(res.data?.roleId ?? null);
    } catch {
      setPermissions([]);
      setRole(null);
      setRoleId(null);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      setLoaded(false);
      load();
    } else {
      setPermissions([]);
      setRole(null);
      setRoleId(null);
      setLoaded(false);
    }
  }, [isAuthenticated, load]);

  // İzin listesi doluysa "kurulmuş" say. Boşsa (rolüne izin atanmamış / sistem henüz
  // canlıda kurulmamış) permissive davran → mevcut ekranlar gizlenmesin (kademeli geçiş).
  const configured = permissions.length > 0;

  const has = useCallback(
    (code: string) => (!configured ? true : permissions.includes(code)),
    [configured, permissions],
  );

  const hasScreen = useCallback(
    (screenCode: string) =>
      !configured ? true : permissions.some((p) => p.startsWith(`${screenCode}:`)),
    [configured, permissions],
  );

  return (
    <PermissionContext.Provider
      value={{ permissions, role, roleId, loaded, configured, has, hasScreen, refresh: load }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionState => {
  const ctx = useContext(PermissionContext);
  if (!ctx)
    throw new Error("usePermissions must be used within a PermissionProvider");
  return ctx;
};
